import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthCtx } from '../App';
import { toast } from 'react-toastify';
import Logo from '../components/Logo';
import PasswordInput from '../components/PasswordInput';
import api from '../utils/api';
import { getStoredRef, clearStoredRef } from '../utils/referral';

// Versioned label for the account-terms consent captured at signup. Bump this
// whenever the account agreement wording (or the Terms sections it references)
// materially changes, so the server-side consent record reflects what was shown.
const ACCOUNT_TERMS_VERSION = '2026-07-06';

export default function RegisterPage() {
  const [form, setForm] = useState({
    firstName: '', lastName: '', dateOfBirth: '', email: '', password: '',
  });
  const [agreed, setAgreed]   = useState(false);
  const [errors, setErrors]   = useState({});
  const { register, login, loading } = useAuthCtx();
  const navigate = useNavigate();
  const referralCode = getStoredRef();

  const computeAge = (isoDate) => {
    const dob = new Date(isoDate);
    if (Number.isNaN(dob.getTime())) return NaN;
    const now = new Date();
    let age = now.getUTCFullYear() - dob.getUTCFullYear();
    const m = now.getUTCMonth() - dob.getUTCMonth();
    if (m < 0 || (m === 0 && now.getUTCDate() < dob.getUTCDate())) age--;
    return age;
  };

  /** Client-side validation */
  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = 'Required';
    if (!form.lastName.trim())  e.lastName  = 'Required';
    if (!form.dateOfBirth) {
      e.dateOfBirth = 'Required';
    } else {
      const age = computeAge(form.dateOfBirth);
      if (Number.isNaN(age))      e.dateOfBirth = 'Invalid date';
      else if (age < 21)          e.dateOfBirth = 'You must be 21 or older to create an account';
      else if (age > 120)         e.dateOfBirth = 'Please enter a valid date of birth';
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Valid email required';
    if (form.password.length < 8)          e.password = 'At least 8 characters';
    if (!/[A-Z]/.test(form.password))      e.password = 'Must include uppercase letter';
    if (!/[0-9]/.test(form.password))      e.password = 'Must include a number';
    if (!agreed) e.agreed = 'You must agree to the Terms of Service to create an account';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    try {
      await register({ ...form, referralCode: referralCode || undefined });
      clearStoredRef();
      await login(form.email, form.password);
      // Best-effort server-side record that this account accepted the Terms
      // (incl. arbitration, class-action waiver, and liability waiver) at signup.
      // A failed POST must never block account creation.
      try {
        await api.post('/legal/consent', {
          termsVersion: ACCOUNT_TERMS_VERSION,
          scope: 'account-registration',
          acceptedClauses: [
            'I agree to the Terms of Service, including binding individual arbitration, ' +
            'a waiver of class actions and jury trial, and the Assumption of Risk & ' +
            'Limitation of Liability.',
            'I understand orders may be held or cancelled if required laboratory / ' +
            'eligibility documentation is not provided.',
          ],
        });
      } catch { /* consent logged server-side elsewhere; never block signup */ }
      toast.success('Welcome to Aloha Amino!');
      navigate('/dashboard');
    } catch (err) {
      if (err.response?.data?.fields) {
        const fe = {};
        err.response.data.fields.forEach(f => { fe[f.field] = f.message; });
        setErrors(fe);
      } else {
        toast.error(err.response?.data?.error || 'Registration failed');
      }
    }
  };

  const set = (f) => (e) => setForm(prev => ({ ...prev, [f]: e.target.value }));

  // Latest DOB that still makes the user 21+ today — used as the date picker's max.
  const maxDob = (() => {
    const d = new Date();
    d.setUTCFullYear(d.getUTCFullYear() - 21);
    return d.toISOString().slice(0, 10);
  })();

  return (
    <div className="min-h-screen bg-tea-50 flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full card">
        <div className="text-center mb-8">
          <Link to="/" aria-label="Aloha Amino home" className="inline-flex items-center">
            <Logo className="text-3xl" />
          </Link>
          <h1 className="mt-4 text-2xl font-bold">Create your account</h1>
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2 mt-3 leading-relaxed">
            Accounts are for licensed researchers and laboratory providers only.
            Products are sold for in-vitro research use.
          </p>
          {referralCode ? (
            <p className="text-xs text-tea-800 bg-tea-50 border border-tea-200 rounded-md p-2 mt-2 leading-relaxed">
              🎉 You were referred by a friend. Create your account and start earning
              <strong> 3% cash back</strong> on every order.
            </p>
          ) : (
            <p className="text-xs text-tea-800 bg-tea-50 border border-tea-200 rounded-md p-2 mt-2 leading-relaxed">
              Members earn <strong>3% cash back</strong> on every order — credited to your
              account after a short hold to spend on your next order.
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* First / Last name row */}
          <div className="grid grid-cols-2 gap-4">
            {[['firstName', 'First Name'], ['lastName', 'Last Name']].map(([f, label]) => (
              <div key={f}>
                <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                <input value={form[f]} onChange={set(f)} className="input-field" placeholder={label} />
                {errors[f] && <p className="text-red-500 text-xs mt-1">{errors[f]}</p>}
              </div>
            ))}
          </div>

          {/* Date of birth — 21+ age gate */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date of Birth
            </label>
            <input
              type="date"
              value={form.dateOfBirth}
              onChange={set('dateOfBirth')}
              max={maxDob}
              className="input-field"
            />
            <p className="text-xs text-gray-500 mt-1">You must be 21 or older to create an account.</p>
            {errors.dateOfBirth && <p className="text-red-500 text-xs mt-1">{errors.dateOfBirth}</p>}
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input type="email" value={form.email} onChange={set('email')} className="input-field"
              placeholder="you@example.com" autoComplete="email" />
            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
          </div>

          {/* Password — with show/hide toggle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <PasswordInput value={form.password} onChange={set('password')}
              placeholder="Min 8 chars, 1 uppercase, 1 number" autoComplete="new-password" />
            {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
          </div>

          {/* Terms consent — conspicuous clickwrap. Key waivers are disclosed up
              front (not buried) so the agreement is actually enforceable. */}
          <div>
            <label className="flex items-start gap-3 cursor-pointer rounded-lg border border-gray-200 p-3 hover:border-tea-300">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-1 h-4 w-4 shrink-0 accent-tea-700"
              />
              <span className="text-xs leading-relaxed text-gray-600">
                I have read and agree to the{' '}
                <Link to="/terms" target="_blank" rel="noreferrer" className="text-tea-700 font-medium underline">
                  Terms of Service
                </Link>{' '}and{' '}
                <Link to="/privacy" target="_blank" rel="noreferrer" className="text-tea-700 font-medium underline">
                  Privacy Policy
                </Link>. I understand this includes a <strong>binding individual arbitration
                agreement</strong> and a <strong>waiver of class actions and jury trial</strong>{' '}
                (Section 8), an <strong>assumption of risk &amp; limitation of liability</strong>{' '}
                (Section 7), and that Aloha Amino <strong>may hold or cancel any order</strong> that
                lacks required laboratory or eligibility documentation (Section 3).
              </span>
            </label>
            {errors.agreed && <p className="text-red-500 text-xs mt-1">{errors.agreed}</p>}
          </div>

          <button type="submit" disabled={loading} className="btn-primary w-full py-3">
            {loading ? 'Creating account...' : 'Create Account'}
          </button>
        </form>

        <p className="text-center mt-6 text-gray-600 text-sm">
          Already have an account?{' '}
          <Link to="/login" className="text-tea-600 font-medium hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
